import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import NotificationToast from '../../../components/ui/NotificationToast';
import hrManagementService from '../../../services/hrManagementService';
import jobApplicationService from '../../../services/jobApplicationService';
import clientRequirementsService from '../../../services/clientRequirementsService';
import employeeService from '../../../services/employeeService';
import { useNavigate } from 'react-router-dom';
import AssignApplicantModal from './AssignApplicantModal';
import ConvertToEmployeeModal from './ConvertToEmployeeModal';
import EmployeeModal from '../../employee-management/components/EmployeeModal';

const HRDashboard = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [selectedHRId, setSelectedHRId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [hrUsers, setHrUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showCreateEmployeeModal, setShowCreateEmployeeModal] = useState(false);
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingEmployee, setCreatingEmployee] = useState(false);
  const [stats, setStats] = useState({
    totalApplicants: 0,
    pendingScreening: 0,
    pendingOffers: 0,
    pendingProbation: 0,
    pendingIncrement: 0,
    pendingExit: 0
  });

  const tabs = [
    { id: 'offer-letters', label: 'Offer Letters', icon: 'FileText' },
    { id: 'probation-letters', label: 'Probation Letters', icon: 'Clock' },
    { id: 'increment-letters', label: 'Increment Letters', icon: 'TrendingUp' },
    { id: 'exit-letters', label: 'Exit Letters', icon: 'LogOut' }
  ];

  useEffect(() => {
    loadHRUsers();
    loadClients();
  }, []);

  useEffect(() => {
    loadStats();
  }, [selectedHRId, selectedClientId]);

  const loadHRUsers = async () => {
    try {
      const result = await hrManagementService.getHRUsers();
      if (result.success) {
        setHrUsers(result.data || []);
      }
    } catch (error) {
      console.error('Error loading HR users:', error);
    }
  };

  const loadClients = async () => {
    try {
      const result = await clientRequirementsService.getClients();
      if (result.success) {
        setClients(result.data || []);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      // Load stats based on filters
      const filters = {};
      if (selectedHRId) filters.assigned_hr_id = selectedHRId;
      if (selectedClientId) filters.client_id = selectedClientId;

      // Load applicants for stats
      const applicantsResult = await jobApplicationService.getApplications(filters);
      if (applicantsResult.success) {
        const applicantsData = applicantsResult.data || [];
        setApplicants(applicantsData);

        // Calculate stats
        setStats({
          totalApplicants: applicantsData.length,
          pendingScreening: applicantsData.filter(a =>
            ['pending', 'screening', 'interview'].includes(a.status)
          ).length,
          pendingOffers: 0, // Will be loaded from offer letters
          pendingProbation: 0, // Will be loaded from probation letters
          pendingIncrement: 0, // Will be loaded from increment letters
          pendingExit: 0 // Will be loaded from exit letters
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignApplicants = () => {
    if (selectedApplicants.length === 0) {
      showNotification('Please select applicants to assign', 'error');
      return;
    }
    setShowAssignModal(true);
  };

  const handleConvertToEmployees = () => {
    if (selectedApplicants.length === 0) {
      showNotification('Please select applicants to convert', 'error');
      return;
    }
    setShowConvertModal(true);
  };

  const handleCreateEmployee = async (employeeData) => {
    try {
      setCreatingEmployee(true);
      const result = await employeeService.createEmployee(employeeData);
      if (result?.success) {
        showNotification('Employee created successfully', 'success');
        setShowCreateEmployeeModal(false);
      } else {
        showNotification(result?.error || 'Failed to create employee', 'error');
      }
    } catch (error) {
      showNotification(error?.message || 'Failed to create employee', 'error');
    } finally {
      setCreatingEmployee(false);
    }
  };

  const handleAssignmentComplete = async () => {
    setShowAssignModal(false);
    setSelectedApplicants([]);
    // Small delay to ensure database update is complete
    await new Promise(resolve => setTimeout(resolve, 500));
    await loadStats();
    showNotification('Applicants assigned successfully', 'success');
  };

  const handleConversionComplete = async () => {
    setShowConvertModal(false);
    setSelectedApplicants([]);
    await loadStats();
    showNotification('Applicants converted to employees successfully', 'success');
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Note: The actual tab components are rendered via routing in the parent HR index.jsx
  // This dashboard provides a centralized view with filters and quick actions

  return (
    <div className="space-y-6">
      <NotificationToast
        message={notification?.message}
        type={notification?.type}
        onDismiss={() => setNotification(null)}
      />

      {/* Dashboard Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">HR Dashboard</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Centralized view of all HR processes with HR-wise and client-wise filtering
          </p>
        </div>
        <Button
          onClick={() => setShowCreateEmployeeModal(true)}
          iconName="UserPlus"
          iconPosition="left"
          disabled={creatingEmployee}
        >
          Create Employee
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Applicants</p>
              <p className="text-2xl font-bold text-foreground mt-1">{stats.totalApplicants}</p>
            </div>
            <Icon name="Users" size={24} className="text-primary" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending Screening</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">{stats.pendingScreening}</p>
            </div>
            <Icon name="UserCheck" size={24} className="text-orange-600" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending Offers</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{stats.pendingOffers}</p>
            </div>
            <Icon name="FileText" size={24} className="text-blue-600" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending Probation</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">{stats.pendingProbation}</p>
            </div>
            <Icon name="Clock" size={24} className="text-purple-600" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending Increment</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{stats.pendingIncrement}</p>
            </div>
            <Icon name="TrendingUp" size={24} className="text-green-600" />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending Exit</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{stats.pendingExit}</p>
            </div>
            <Icon name="LogOut" size={24} className="text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Filter by HR Personnel
            </label>
            <Select
              value={selectedHRId}
              onChange={(value) => setSelectedHRId(value)}
              options={[
                { value: '', label: 'All HR Personnel' },
                ...hrUsers.map(hr => ({
                  value: hr.id,
                  label: hr.full_name
                }))
              ]}
              placeholder="Select HR Personnel"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Filter by Client
            </label>
            <Select
              value={selectedClientId}
              onChange={(value) => setSelectedClientId(value)}
              options={[
                { value: '', label: 'All Clients' },
                ...clients.map(client => ({
                  value: client.id,
                  label: client.company_name || client.name
                }))
              ]}
              placeholder="Select Client"
            />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedHRId('');
                setSelectedClientId('');
              }}
              className="w-full"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>


      {/* Quick Access Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => {
              // Navigate to the corresponding HR tab route
              const routes = {
                'offer-letters': '/erp/applicant-tracking-system/offer-letters',
                'probation-letters': '/erp/applicant-tracking-system/probation-letters',
                'increment-letters': '/erp/hr/increment-letters',
                'exit-letters': '/erp/hr/exit-letters'
              };
              if (routes[tab.id]) {
                navigate(routes[tab.id]);
              }
            }}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name={tab.icon} size={24} className="text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">{tab.label}</p>
                <p className="text-xs text-muted-foreground">Manage {tab.label.toLowerCase()}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Summary */}
      <div className="bg-card border border-border rounded-lg p-4">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Active Filters:</span> {' '}
          {selectedHRId ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded">
              HR: {hrUsers.find(h => h.id === selectedHRId)?.full_name}
              <button onClick={() => setSelectedHRId('')} className="hover:text-primary-dark">
                <Icon name="X" size={12} />
              </button>
            </span>
          ) : (
            <span>All HR Personnel</span>
          )}{' '}
          | {' '}
          {selectedClientId ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded">
              Client: {clients.find(c => c.id === selectedClientId)?.company_name || clients.find(c => c.id === selectedClientId)?.name}
              <button onClick={() => setSelectedClientId('')} className="hover:text-primary-dark">
                <Icon name="X" size={12} />
              </button>
            </span>
          ) : (
            <span>All Clients</span>
          )}
        </p>
      </div>

      {/* Applicants Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Applicants</h3>
              <p className="text-sm text-muted-foreground">Select applicants to assign to HR or convert to employees</p>
            </div>
            {selectedApplicants.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleAssignApplicants}
                  iconName="UserCheck"
                  iconPosition="left"
                >
                  Assign to HR ({selectedApplicants.length})
                </Button>
                <Button
                  onClick={handleConvertToEmployees}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Convert to Employees ({selectedApplicants.length})
                </Button>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground text-sm">Loading applicants...</p>
          </div>
        ) : applicants.length > 0 ? (
          <>
            {/* Bulk Actions Bar */}
            {selectedApplicants.length > 0 && (
              <div className="bg-primary/10 border-b border-border p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">
                    {selectedApplicants.length} applicant(s) selected
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedApplicants([])}
                    iconName="X"
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="w-12 p-4">
                      <input
                        type="checkbox"
                        checked={selectedApplicants.length === applicants.length && applicants.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedApplicants(applicants.map(a => a.id));
                          } else {
                            setSelectedApplicants([]);
                          }
                        }}
                        className="rounded border-input"
                      />
                    </th>
                    <th className="text-left p-4 font-medium text-foreground">Applicant</th>
                    <th className="text-left p-4 font-medium text-foreground">Position</th>
                    <th className="text-left p-4 font-medium text-foreground">Status</th>
                    <th className="text-left p-4 font-medium text-foreground">HR Assigned</th>
                    <th className="text-left p-4 font-medium text-foreground">Client</th>
                    <th className="text-left p-4 font-medium text-foreground">Applied Date</th>
                  </tr>
                </thead>
                <tbody>
                  {applicants.map((applicant) => (
                    <tr
                      key={applicant.id}
                      className="border-b border-border hover:bg-muted/30 transition-colors"
                    >
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={selectedApplicants.includes(applicant.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (selectedApplicants.includes(applicant.id)) {
                              setSelectedApplicants(selectedApplicants.filter(id => id !== applicant.id));
                            } else {
                              setSelectedApplicants([...selectedApplicants, applicant.id]);
                            }
                          }}
                          className="rounded border-input"
                        />
                      </td>
                      <td className="p-4">
                        <div>
                          <p className="font-medium text-foreground">
                            {applicant.applicant_name || applicant.name || `${applicant.first_name || ''} ${applicant.last_name || ''}`.trim() || 'N/A'}
                          </p>
                          <p className="text-sm text-muted-foreground">{applicant.email || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-foreground">{applicant.position || 'N/A'}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${(applicant.status || applicant.screening_status) === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          (applicant.status || applicant.screening_status) === 'approved' ? 'bg-green-100 text-green-800' :
                            (applicant.status || applicant.screening_status) === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                          }`}>
                          {(applicant.status || applicant.screening_status || 'pending').charAt(0).toUpperCase() + (applicant.status || applicant.screening_status || 'pending').slice(1)}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-foreground">
                          {applicant.assigned_hr_name || 'Unassigned'}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-foreground">
                          {applicant.client_name || 'N/A'}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-foreground">
                          {applicant.applied_date || applicant.created_at
                            ? new Date(applicant.applied_date || applicant.created_at).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                            : 'N/A'}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground">No applicants found</p>
            {selectedHRId || selectedClientId ? (
              <p className="text-sm text-muted-foreground mt-2">Try adjusting your filters</p>
            ) : null}
          </div>
        )}
      </div>

      {/* Assign Applicant Modal */}
      {showAssignModal && (
        <AssignApplicantModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          applicantIds={selectedApplicants}
          hrUsers={hrUsers}
          onComplete={handleAssignmentComplete}
        />
      )}

      {/* Convert to Employee Modal */}
      {showConvertModal && (
        <ConvertToEmployeeModal
          isOpen={showConvertModal}
          onClose={() => setShowConvertModal(false)}
          applicants={applicants.filter(a => selectedApplicants.includes(a.id))}
          clients={clients}
          onComplete={handleConversionComplete}
        />
      )}

      {/* Create Employee Modal */}
      {showCreateEmployeeModal && (
        <EmployeeModal
          isOpen={showCreateEmployeeModal}
          onClose={() => setShowCreateEmployeeModal(false)}
          mode="add"
          onSave={handleCreateEmployee}
        />
      )}
    </div>
  );
};

export default HRDashboard;

